import { Link } from "@tanstack/react-router";
import type { StepView } from "@/app/queries";
import { bankStats, useQuestionBanks } from "@/app/questionQueries";
import { plural } from "@/lib/plural";
import { Button } from "@/ui/Button";
import { GATE_BADGE, canSubmit, criteriaLabel, submitReason } from "./model";

const tone = {
  warn: "border-warn bg-warnsoft text-warn",
  ok: "border-ok bg-oksoft text-ok",
};

/** Бейдж состояния гейта: «гейт не сдан» · «ждёт ментора» · «гейт сдан». */
export function GateBadge({ status, label, testId = "gate-status" }: { status: StepView["gate_status"]; label?: string; testId?: string }) {
  const b = GATE_BADGE[status];
  return (
    <span data-testid={testId} data-gate={status} className={`inline-flex items-center gap-1.5 rounded-sm border px-2 py-[2px] font-mono text-[11px] ${tone[b.tone]}`}>
      <span aria-hidden className="size-1.5 rounded-full bg-current" />
      {label ?? b.label}
    </span>
  );
}

/** Карточка «Готов к переходу» (S4/S8): критерии ✓/○, банк вопросов и
 *  кнопка «сдать гейт» — рядом всегда причина (правило 3). */
export function GateCard({ view, onSubmit, pending }: { view: StepView; onSubmit: () => void; pending: boolean }) {
  const g = view.gate;
  const banks = useQuestionBanks();
  const stats = bankStats(banks.data, view.content.gate.bank.name, plural);
  const enabled = canSubmit(view) && !pending;
  const reason = pending ? "отправляем…" : submitReason(view);
  return (
    <section id="gate" className="scroll-mt-20 overflow-hidden rounded-md border border-bd2 bg-card" data-testid="gate">
      <div className="flex flex-wrap items-center gap-2.5 border-b border-bd bg-bg2 px-4 py-3">
        <span className="font-mono text-[11px] uppercase tracking-[.06em] text-mut">готов к переходу</span>
        <GateBadge status={view.gate_status} />
        <span className="flex-1" />
        <span className="font-mono text-[11.5px] text-mut" data-testid="gate-frac">
          {g.met} / {criteriaLabel(g.total)}
        </span>
      </div>
      <ul className="m-0 flex list-none flex-col gap-2.5 border-b border-bd px-4 py-3.5">
        {g.criteria.map((c) => (
          <li key={c.id} className="grid grid-cols-[18px_minmax(0,1fr)] items-start gap-[11px]" data-testid="criterion" data-kind={c.kind} data-met={c.met}>
            <span aria-hidden className={`font-mono text-[12px] ${c.met ? "text-ok" : "text-mut"}`}>
              {c.met ? "✓" : "○"}
            </span>
            <span className="text-pretty text-[13px] leading-[1.5] text-ink2">
              <span className="sr-only">{c.met ? "выполнено: " : "не выполнено: "}</span>
              {c.text}
              {!c.auto && <span className="ml-2 whitespace-nowrap font-mono text-[10.5px] text-mut">отмечает ментор</span>}
            </span>
          </li>
        ))}
      </ul>
      <div className="flex flex-wrap items-center gap-3.5 px-4 py-3.5">
        <span className="font-mono text-[11px] text-mut">банк вопросов</span>
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <Link to="/questions" search={{ bank: view.content.gate.bank.name }} className="font-mono text-[13px] text-acc no-underline hover:underline">
            ↗ Реальные вопросы с собеседований: «{view.content.gate.bank.name}»
          </Link>
          {stats && (
            <span className="font-mono text-[11px] text-ink2" data-testid="bank-stats">
              {stats}
            </span>
          )}
        </span>
        {view.gate_status === "open" ? (
          <Button variant="primary" disabled={!enabled} reason={reason} onClick={onSubmit} data-testid="submit-gate">
            сдать гейт
          </Button>
        ) : (
          <GateBadge status={view.gate_status} testId="gate-chip" />
        )}
      </div>
    </section>
  );
}
